/**
 * Country codes, canonicalised. (F34)
 *
 * ─── The canonical form is ISO 3166-1 ALPHA-2 ─────────────────────────────
 *
 * Every country column the platform reads as a place — `journey_instances
 * .origin_country` / `.destination_country`, `talents.current_country` — is
 * alpha-2: `COUNTRIES` is keyed on it, the journey builder validates it
 * (`countryCodeSchema`, length 2), and the locale catalogue names countries by
 * it (`common.countries.XX`). The columns are `varchar(3)` for the destination
 * sentinel `UNK`, not to hold a second code system.
 *
 * They held one anyway. The Pool import validated `currentCountry` as ALPHA-3
 * and stored it as sent, and two writers then copied `talents.current_country`
 * straight into a journey's origin (QultureUp auto-create; invitation
 * acceptance, "origin = residence"). So `origin_country` held `BR` for one
 * citizen and `BRA` for the next, and every reader that looked a code up —
 * the journey selector's country name, its flag, a translated label —
 * rendered the raw `BRA` for the second one.
 *
 * `toAlpha2CountryCode` is the one conversion. Writers call it at the
 * boundary (the Pool import schema, the journey factory); migration 0165
 * rewrites the rows written before it existed. Readers never call it: after
 * both, there is nothing to convert, and a reader that "tolerated" alpha-3
 * would hide the next writer that forgot.
 *
 * `ALPHA3_TO_ALPHA2` covers exactly the countries in `COUNTRIES` — the set the
 * product offers anywhere — and `country-codes.spec.ts` asserts that coverage
 * in both directions, so a country added to one and not the other fails the
 * build rather than rendering a raw code.
 */

import { UNKNOWN_DESTINATION } from './countries';

/** ISO 3166-1 alpha-3 → alpha-2, for every country in `COUNTRIES`. */
export const ALPHA3_TO_ALPHA2: Readonly<Record<string, string>> = Object.freeze({
  AFG: 'AF',
  ALB: 'AL',
  DZA: 'DZ',
  AND: 'AD',
  AGO: 'AO',
  ATG: 'AG',
  ARG: 'AR',
  ARM: 'AM',
  AUS: 'AU',
  AUT: 'AT',
  AZE: 'AZ',
  BHS: 'BS',
  BHR: 'BH',
  BGD: 'BD',
  BRB: 'BB',
  BLR: 'BY',
  BEL: 'BE',
  BLZ: 'BZ',
  BEN: 'BJ',
  BTN: 'BT',
  BOL: 'BO',
  BIH: 'BA',
  BWA: 'BW',
  BRA: 'BR',
  BRN: 'BN',
  BGR: 'BG',
  BFA: 'BF',
  BDI: 'BI',
  CPV: 'CV',
  KHM: 'KH',
  CMR: 'CM',
  CAN: 'CA',
  CAF: 'CF',
  TCD: 'TD',
  CHL: 'CL',
  CHN: 'CN',
  COL: 'CO',
  COM: 'KM',
  COG: 'CG',
  COD: 'CD',
  CRI: 'CR',
  CIV: 'CI',
  HRV: 'HR',
  CUB: 'CU',
  CYP: 'CY',
  CZE: 'CZ',
  DNK: 'DK',
  DJI: 'DJ',
  DMA: 'DM',
  DOM: 'DO',
  ECU: 'EC',
  EGY: 'EG',
  SLV: 'SV',
  GNQ: 'GQ',
  ERI: 'ER',
  EST: 'EE',
  SWZ: 'SZ',
  ETH: 'ET',
  FJI: 'FJ',
  FIN: 'FI',
  FRA: 'FR',
  GAB: 'GA',
  GMB: 'GM',
  GEO: 'GE',
  DEU: 'DE',
  GHA: 'GH',
  GRC: 'GR',
  GRD: 'GD',
  GTM: 'GT',
  GIN: 'GN',
  GNB: 'GW',
  GUY: 'GY',
  HTI: 'HT',
  HND: 'HN',
  HKG: 'HK',
  HUN: 'HU',
  ISL: 'IS',
  IND: 'IN',
  IDN: 'ID',
  IRN: 'IR',
  IRQ: 'IQ',
  IRL: 'IE',
  ISR: 'IL',
  ITA: 'IT',
  JAM: 'JM',
  JPN: 'JP',
  JOR: 'JO',
  KAZ: 'KZ',
  KEN: 'KE',
  KIR: 'KI',
  KWT: 'KW',
  KGZ: 'KG',
  LAO: 'LA',
  LVA: 'LV',
  LBN: 'LB',
  LSO: 'LS',
  LBR: 'LR',
  LBY: 'LY',
  LIE: 'LI',
  LTU: 'LT',
  LUX: 'LU',
  MAC: 'MO',
  MDG: 'MG',
  MWI: 'MW',
  MYS: 'MY',
  MDV: 'MV',
  MLI: 'ML',
  MLT: 'MT',
  MHL: 'MH',
  MRT: 'MR',
  MUS: 'MU',
  MEX: 'MX',
  FSM: 'FM',
  MDA: 'MD',
  MCO: 'MC',
  MNG: 'MN',
  MNE: 'ME',
  MAR: 'MA',
  MOZ: 'MZ',
  MMR: 'MM',
  NAM: 'NA',
  NRU: 'NR',
  NPL: 'NP',
  NLD: 'NL',
  NZL: 'NZ',
  NIC: 'NI',
  NER: 'NE',
  NGA: 'NG',
  PRK: 'KP',
  MKD: 'MK',
  NOR: 'NO',
  OMN: 'OM',
  PAK: 'PK',
  PLW: 'PW',
  PSE: 'PS',
  PAN: 'PA',
  PNG: 'PG',
  PRY: 'PY',
  PER: 'PE',
  PHL: 'PH',
  POL: 'PL',
  PRT: 'PT',
  QAT: 'QA',
  ROU: 'RO',
  RUS: 'RU',
  RWA: 'RW',
  KNA: 'KN',
  LCA: 'LC',
  VCT: 'VC',
  WSM: 'WS',
  SMR: 'SM',
  STP: 'ST',
  SAU: 'SA',
  SEN: 'SN',
  SRB: 'RS',
  SYC: 'SC',
  SLE: 'SL',
  SGP: 'SG',
  SVK: 'SK',
  SVN: 'SI',
  SLB: 'SB',
  SOM: 'SO',
  ZAF: 'ZA',
  KOR: 'KR',
  SSD: 'SS',
  ESP: 'ES',
  LKA: 'LK',
  SDN: 'SD',
  SUR: 'SR',
  SWE: 'SE',
  CHE: 'CH',
  SYR: 'SY',
  TWN: 'TW',
  TJK: 'TJ',
  TZA: 'TZ',
  THA: 'TH',
  TLS: 'TL',
  TGO: 'TG',
  TON: 'TO',
  TTO: 'TT',
  TUN: 'TN',
  TUR: 'TR',
  TKM: 'TM',
  TUV: 'TV',
  UGA: 'UG',
  UKR: 'UA',
  ARE: 'AE',
  GBR: 'GB',
  USA: 'US',
  URY: 'UY',
  UZB: 'UZ',
  VUT: 'VU',
  VAT: 'VA',
  VEN: 'VE',
  VNM: 'VN',
  YEM: 'YE',
  ZMB: 'ZM',
  ZWE: 'ZW',
});

/**
 * The canonical ALPHA-2 code for a country code in either ISO form.
 *
 *   • 2 letters → the same code, upper-cased. Not checked against `COUNTRIES`:
 *     that list is what the product OFFERS, not the whole of ISO 3166, and a
 *     writer must not erase a real code merely because no picker lists it.
 *   • 3 letters → its alpha-2 from {@link ALPHA3_TO_ALPHA2}, or `null` when it
 *     is not a code the product knows. `null`, not the input: a writer must
 *     decide what to store for an unknown code rather than have it pass through
 *     under a canonical name.
 *   • anything else (empty, the `UNK` sentinel is handled by callers before
 *     here, free text) → `null`.
 *
 * Case- and whitespace-insensitive, because the Pool import accepts a CSV cell.
 */
export function toAlpha2CountryCode(code: string | null | undefined): string | null {
  if (!code) return null;
  const upper = code.trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return upper;
  if (/^[A-Z]{3}$/.test(upper)) return ALPHA3_TO_ALPHA2[upper] ?? null;
  return null;
}

/**
 * A journey route with both countries in canonical form — the journey
 * factory's write boundary (F34).
 *
 *   • origin: alpha-2, or `null` when the code is not a country the product
 *     knows. `origin_country` is nullable and NULL is its honest "unknown".
 *   • destination: alpha-2; the `UNK` sentinel stays `UNK`; a code that is not
 *     a known country BECOMES `UNK`. `destination_country` is NOT NULL, and the
 *     sentinel is exactly "we do not know where", which the journey factory
 *     already treats as "not a route".
 *
 * Returns the SAME object when nothing changes, so a caller can tell "already
 * canonical" from "rewritten" by identity.
 */
export function canonicalRouteCountries<
  T extends { originCountry: string | null; destinationCountry: string },
>(route: T): T {
  const origin = route.originCountry === null ? null : toAlpha2CountryCode(route.originCountry);
  const destination =
    route.destinationCountry === UNKNOWN_DESTINATION
      ? UNKNOWN_DESTINATION
      : (toAlpha2CountryCode(route.destinationCountry) ?? UNKNOWN_DESTINATION);
  if (origin === route.originCountry && destination === route.destinationCountry) return route;
  return { ...route, originCountry: origin, destinationCountry: destination };
}
